import QRCode from "qrcode";
import { jsPDF } from "jspdf";
import * as api from "./api";
import { STATUSES, STATUSES_SUR_PLACE } from "./constants";

export function formatPhone(phone) {
  const p = phone.replace(/[\s\-().]/g, "");
  if (p.startsWith("+"))   return p.slice(1);
  if (p.startsWith("00"))  return p.slice(2);
  if (p.startsWith("221")) return p;
  return "221" + p;
}

export function sendWa(phone, msg) {
  const url = `https://wa.me/${formatPhone(phone)}?text=${encodeURIComponent(msg)}`;
  const a = document.createElement("a");
  a.href = url; a.target = "_blank";
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
}

export function waMessage(order) {
  const isSurPlace = order.order_type === "sur_place";
  const price = Number(order.price) > 0 ? ` — *${Number(order.price).toLocaleString()} FCFA*` : "";
  const id = `*${order.id}*`;
  const sign = "\n— _Setal Express_ 🧺";

  const msgs = {
    // ── Sur place : client apporte son linge en boutique ──────────────────
    "Réceptionné":
      `Bonjour *${order.client}* 👋\nVotre linge a bien été réceptionné en boutique — ${id} 📦\nNous en prenons soin dès maintenant.${sign}`,

    // ── Collecte : collecteur passe chez le client ─────────────────────────
    "Collecté":
      `Bonjour *${order.client}* 👋\nVotre linge a été collecté à domicile — ${id} 🛵\nNous en prenons soin. Vous serez notifié à chaque étape.${sign}`,

    // ── Traitement : identique pour les deux types ──────────────────────────
    "En traitement":
      `Bonjour *${order.client}* ✨\nVotre commande ${id} est en cours de traitement.\nNos experts s'occupent de votre linge avec soin.${sign}`,

    // ── Prêt : sur place -> venez chercher / collecte -> livraison imminente ─
    "Prêt": isSurPlace
      ? `Bonjour *${order.client}* 🎉\nVos vêtements sont prêts !${price}\n🏪 *Vous pouvez venir les récupérer en boutique.*${sign}`
      : `Bonjour *${order.client}* 🎉\nVos vêtements sont prêts !${price}\n🚚 *Nous vous livrons très bientôt à domicile.*${sign}`,

    // ── Livré : uniquement collecte ─────────────────────────────────────────
    "Livré":
      `Bonjour *${order.client}* ✅\nVotre commande ${id} a été *livrée à domicile* !\nMerci de votre confiance 🙏${sign}`,

    // ── Récupéré : sur place -> client venu chercher / collecte -> récupéré ─
    "Récupéré": isSurPlace
      ? `Bonjour *${order.client}* ✅\nVos vêtements ont bien été récupérés en boutique — ${id}\nMerci de votre confiance 🙏${sign}`
      : `Bonjour *${order.client}* ✅\nVotre commande ${id} a été récupérée !\nMerci de votre confiance 🙏${sign}`,
  };

  return msgs[order.status] || `Bonjour *${order.client}*, votre commande ${id} a été mise à jour.${sign}`;
}

// ─── Gestion du consentement WhatsApp (fenêtre 24h) ──────────────────────────
const WA_TTL = 24 * 60 * 60 * 1000; // 24 heures en ms

export function shouldAskWaConsent(phone) {
  try {
    const raw = localStorage.getItem(`wa_consent_${phone}`);
    if (!raw) return true; // premier contact
    const { opted_in, last_sent } = JSON.parse(raw);
    if (!opted_in) return true; // avait refusé
    return (Date.now() - last_sent) > WA_TTL; // fenêtre expirée -> redemander
  } catch { return true; }
}

export function markWaSent(phone) {
  try {
    localStorage.setItem(`wa_consent_${phone}`, JSON.stringify({ opted_in: true, last_sent: Date.now() }));
  } catch {}
}

export function markWaDeclined(phone) {
  try {
    const raw = localStorage.getItem(`wa_consent_${phone}`);
    const existing = raw ? JSON.parse(raw) : {};
    localStorage.setItem(`wa_consent_${phone}`, JSON.stringify({ ...existing, opted_in: false }));
  } catch {}
}

export function waWindowRemaining(phone) {
  try {
    const raw = localStorage.getItem(`wa_consent_${phone}`);
    if (!raw) return null;
    const { opted_in, last_sent } = JSON.parse(raw);
    if (!opted_in) return null;
    const remaining = WA_TTL - (Date.now() - last_sent);
    return remaining > 0 ? remaining : null;
  } catch { return null; }
}

export function nextStatus(s, orderType) {
  if (orderType === "sur_place") {
    const i = STATUSES_SUR_PLACE.indexOf(s);
    return i >= 0 && i < STATUSES_SUR_PLACE.length - 1 ? STATUSES_SUR_PLACE[i + 1] : null;
  }
  const i = STATUSES.indexOf(s);
  return i >= 0 && i < STATUSES.length - 1 ? STATUSES[i + 1] : null;
}

export function trackUrl(id) {
  return `${window.location.origin}?track=${id}`;
}

export async function qrDataUrl(orderId) {
  try {
    return await QRCode.toDataURL(trackUrl(orderId), { width: 2048, margin: 1, errorCorrectionLevel: "H", color: { dark: "#000000", light: "#ffffff" } });
  } catch { return null; }
}

export function fmtPdf(n) {
  return Number(n).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// ─── Génération PDF avec jsPDF ────────────────────────────────────────────────
// ─── Default receipt font size profiles ───────────────────────────────────
export const DEFAULT_RECEIPT_SIZES = {
  normal: {
    title: 60, subtitle: 24, orderId: 54, statusBadge: 30,
    clientInfo: 32, articleHeader: 32, articleRow: 30,
    subtotal: 30, total: 48, avanceReste: 30, resteBig: 34,
    payeBadge: 28, qrLabel: 22, footerTitle: 24, footerInfo: 20, footerDate: 18,
  },
  grand: {
    title: 68, subtitle: 27, orderId: 62, statusBadge: 34,
    clientInfo: 36, articleHeader: 36, articleRow: 34,
    subtotal: 34, total: 42, avanceReste: 30, resteBig: 34,
    payeBadge: 32, qrLabel: 25, footerTitle: 27, footerInfo: 23, footerDate: 21,
  },
};

export const RECEIPT_SIZE_LABELS = {
  title: "Titre (SETAL EXPRESS)",
  subtitle: "Sous-titre",
  orderId: "Numéro commande",
  statusBadge: "Badge statut",
  clientInfo: "Infos client",
  articleHeader: "En-tête articles",
  articleRow: "Lignes articles",
  subtotal: "Sous-total / Réductions",
  total: "TOTAL",
  avanceReste: "Avance / Reste",
  resteBig: "Bloc Reste (gros)",
  payeBadge: "Badge PAYÉ",
  qrLabel: "Texte QR code",
  footerTitle: "Pied de page titre",
  footerInfo: "Pied de page infos",
  footerDate: "Pied de page date",
};

// Cached receipt config from backend
let _receiptSizesCache = null;
export function setReceiptSizesCache(config) { _receiptSizesCache = config; }
export function getReceiptSizesCache() { return _receiptSizesCache; }

// Cached branding config (phone numbers etc.)
let _brandingCache = null;
export function setBrandingCache(config) { _brandingCache = config; }
export function getBrandingCache() { return _brandingCache; }

export async function generateReceiptPDF(order, format = "a4", sizeProfile = "normal") {
  const formats = { a5: { format: "a5", orientation: "portrait" }, a4: { format: "a4", orientation: "portrait" }, ticket: { format: [80, 200], orientation: "portrait" } };
  const fmt = formats[format] || formats.a4;
  const doc = new jsPDF({ unit: "mm", ...fmt });

  // Font size profile
  const savedSizes = _receiptSizesCache || {};
  const profileSizes = savedSizes[sizeProfile] || DEFAULT_RECEIPT_SIZES[sizeProfile] || DEFAULT_RECEIPT_SIZES.normal;
  const sz = { ...(DEFAULT_RECEIPT_SIZES[sizeProfile] || DEFAULT_RECEIPT_SIZES.normal), ...profileSizes };

  // Load items: try API first to get full data with prices
  let items = [];
  let itemsHavePrices = false;
  try {
    const orderItems = await api.getOrderItems(order.id);
    if (orderItems && orderItems.length) {
      items = orderItems.map(i => ({
        name: i.article_name,
        qty: i.quantity,
        unit_price: Number(i.unit_price) || null,
        total_price: Number(i.total_price) || (i.quantity * Number(i.unit_price)) || null,
      }));
      itemsHavePrices = true;
    }
  } catch {}
  // Fallback to order.items string (no price data)
  if (items.length === 0 && order.items) {
    items = order.items.split(",").filter(Boolean).map(item => {
      const parts = item.trim().split(" x");
      return { name: parts[0]?.trim() || item.trim(), qty: parts[1]?.trim() || "1", unit_price: null, total_price: null };
    });
  }
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const isTicket = format === "ticket";
  const margin = isTicket ? 4 : 16;

  // Helper: check if we need a new page
  function checkPage(needed) {
    if (!isTicket && y + needed > H - 10) {
      doc.addPage();
      y = 15;
    }
  }

  // Scale factor: ratio of current profile vs "normal" baseline
  const baseTitle = DEFAULT_RECEIPT_SIZES.normal.title; // 60
  const scale = sz.title / baseTitle;
  const S = (v) => Math.round(v * scale);

  // Header — scaled height
  const headerH = S(66);
  const headerAccentY = headerH - S(8);
  doc.setFillColor(15, 45, 110);
  doc.rect(0, 0, W, isTicket ? 22 : headerH, "F");
  doc.setFillColor(26, 86, 219);
  doc.rect(0, isTicket ? 18 : headerAccentY, W, isTicket ? 4 : S(8), "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(isTicket ? 14 : sz.title);
  doc.setFont("helvetica", "bold");
  doc.text("SETAL EXPRESS", W / 2, isTicket ? 10 : headerH * 0.52, { align: "center" });
  doc.setFontSize(isTicket ? 6 : sz.subtitle);
  doc.setFont("helvetica", "normal");
  doc.text("Service de pressing premium - Dakar", W / 2, isTicket ? 16 : headerH * 0.76, { align: "center" });

  let y = isTicket ? 28 : headerH + S(22);
  doc.setFontSize(isTicket ? 7 : sz.clientInfo);
  doc.setTextColor(100, 116, 139);
  doc.text("RECU DE COLLECTE", W / 2, y, { align: "center" });
  y += isTicket ? 5 : S(20);
  doc.setFontSize(isTicket ? 14 : sz.orderId);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 45, 110);
  doc.text(order.id, W / 2, y, { align: "center" });
  y += isTicket ? 6 : S(22);

  // Status badge
  const sc = { "En attente": [245,158,11], "En traitement": [249,115,22], "Pret": [34,197,94], "Livre": [22,163,74] }[order.status] || [26,86,219];
  doc.setFillColor(sc[0], sc[1], sc[2]);
  doc.setFontSize(isTicket ? 6 : sz.statusBadge);
  doc.setFont("helvetica", "bold");
  const sw = doc.getTextWidth(order.status) + S(20);
  const badgeH = isTicket ? 5 : S(19);
  doc.roundedRect(W / 2 - sw / 2, y - badgeH * 0.37, sw, badgeH, 4, 4, "F");
  doc.setTextColor(255, 255, 255);
  doc.text(order.status, W / 2, y + badgeH * 0.47, { align: "center" });
  y += isTicket ? 8 : S(28);

  doc.setDrawColor(200, 210, 230);
  doc.setLineWidth(0.4);
  doc.line(margin, y, W - margin, y);
  doc.setLineWidth(0.2);
  y += isTicket ? 5 : S(18);

  // Client info
  const lineH = isTicket ? 5 : S(18);
  const infoRows = [
    ["Client", order.client],
    ["Telephone", order.phone],
    ["Adresse", order.address || ""],
    ["Date", `${order.date} a ${order.time}`],
  ];
  if (order.payment_method) infoRows.push(["Paiement", { cash: "Especes", wave: "Wave", orange_money: "Orange Money", om: "Orange Money", card: "Carte" }[order.payment_method] || order.payment_method]);
  if (order.payment_status) infoRows.push(["Statut", { non_paye: "Non paye", avance: "Avance", paye: "Paye" }[order.payment_status] || order.payment_status]);

  doc.setFontSize(isTicket ? 7 : sz.clientInfo);
  const labelW = isTicket ? 28 : S(72);
  const valueMaxW = W - margin - (margin + labelW) - 2;
  infoRows.forEach(([label, value]) => {
    checkPage(lineH + 2);
    doc.setFont("helvetica", "bold"); doc.setTextColor(55, 65, 81);
    doc.text(label, margin, y);
    doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
    const valStr = String(value);
    const lines = doc.splitTextToSize(valStr, valueMaxW);
    doc.text(lines, margin + labelW, y);
    y += lineH * lines.length;
  });

  // Articles table
  if (items.length > 0) {
    y += 10;
    checkPage(S(36));
    doc.setDrawColor(200, 210, 230);
    doc.setLineWidth(0.4); doc.line(margin, y, W - margin, y); doc.setLineWidth(0.2);
    y += isTicket ? 5 : S(16);
    const artHeaderH = isTicket ? 6 : S(20);
    doc.setFillColor(245, 247, 252);
    doc.rect(margin, y - artHeaderH * 0.45, W - margin * 2, artHeaderH, "F");
    doc.setFont("helvetica", "bold"); doc.setTextColor(55, 65, 81);
    doc.setFontSize(isTicket ? 7 : sz.articleHeader);

    // Column positions
    const showPrices = itemsHavePrices && !isTicket;
    const colQteX   = showPrices ? W - margin - 6 - S(100) : W - margin - 6;
    const colPUX    = showPrices ? W - margin - 6 - S(50)  : 0;
    const colTotalX = showPrices ? W - margin - 6           : 0;
    const artNameMaxW = showPrices ? colQteX - margin - 12 : W - margin * 2 - S(40);

    doc.text("Article", margin + 6, y + artHeaderH * 0.3);
    doc.text("Qte", colQteX, y + artHeaderH * 0.3, { align: "right" });
    if (showPrices) {
      doc.text("Prix unit.", colPUX, y + artHeaderH * 0.3, { align: "right" });
      doc.text("Total", colTotalX, y + artHeaderH * 0.3, { align: "right" });
    }
    y += isTicket ? 6 : S(22);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(isTicket ? 7 : sz.articleRow);
    const artRowH = isTicket ? 5 : S(18);

    items.forEach((item, i) => {
      const artName  = item.name || "";
      const artQty   = `x${item.qty || "1"}`;
      const artPU    = item.unit_price  != null ? `${fmtPdf(item.unit_price)} F`  : "";
      const artTotal = item.total_price != null ? `${fmtPdf(item.total_price)} F` : "";
      doc.setFont("helvetica", "normal");
      doc.setFontSize(isTicket ? 7 : sz.articleRow);
      const artLines = doc.splitTextToSize(artName, artNameMaxW);
      const rowH = artRowH * artLines.length;
      checkPage(rowH + 2);
      if (i % 2 === 0) { doc.setFillColor(245, 247, 252); doc.rect(margin, y - artRowH * 0.5, W - margin * 2, rowH, "F"); }
      doc.setTextColor(55, 65, 81);
      doc.text(artLines, margin + 6, y);
      doc.setFont("helvetica", "bold");
      doc.text(artQty, colQteX, y, { align: "right" });
      if (showPrices) {
        doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
        doc.text(artPU, colPUX, y, { align: "right" });
        doc.setFont("helvetica", "bold"); doc.setTextColor(15, 45, 110);
        doc.text(artTotal, colTotalX, y, { align: "right" });
      }
      doc.setFont("helvetica", "normal");
      y += rowH;
    });
  }

  // Totals
  y += 10;
  checkPage(S(42));
  doc.setDrawColor(15, 45, 110); doc.setLineWidth(0.8); doc.line(margin, y, W - margin, y); doc.setLineWidth(0.2);
  y += isTicket ? 5 : lineH;

  if (Number(order.loyalty_discount) > 0) {
    const orig = Number(order.price) + Number(order.loyalty_discount);
    doc.setFontSize(isTicket ? 8 : sz.subtotal);
    doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
    doc.text("Sous-total", margin, y); doc.text(`${fmtPdf(orig)} FCFA`, W - margin, y, { align: "right" });
    y += lineH;
    doc.setFont("helvetica", "bold"); doc.setTextColor(22, 163, 74);
    doc.text("Reduction fidelite", margin, y); doc.text(`-${fmtPdf(order.loyalty_discount)} FCFA`, W - margin, y, { align: "right" });
    y += lineH;
  }
  if (Number(order.promo_discount) > 0) {
    doc.setFontSize(isTicket ? 8 : sz.subtotal);
    doc.setFont("helvetica", "bold"); doc.setTextColor(124, 58, 237);
    doc.text(`Code promo (${order.promo_code || ""})`, margin, y); doc.text(`-${fmtPdf(order.promo_discount)} FCFA`, W - margin, y, { align: "right" });
    y += lineH;
  }

  if (order.price) {
    const totalBoxH = isTicket ? 10 : S(36);
    checkPage(totalBoxH + 10);
    doc.setFillColor(15, 45, 110);
    doc.roundedRect(margin, y, W - margin * 2, totalBoxH, 4, 4, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold");
    doc.setFontSize(isTicket ? 10 : sz.total);
    doc.text("TOTAL", margin + 14, y + totalBoxH * 0.72);
    doc.text(`${fmtPdf(order.price)} FCFA`, W - margin - 14, y + totalBoxH * 0.72, { align: "right" });
    y += totalBoxH + S(8);
  }

  // Avance & Restant à payer
  if (order.payment_status === "avance" && Number(order.amount_paid) > 0) {
    checkPage(S(40));
    y += isTicket ? 3 : S(8);
    doc.setFontSize(isTicket ? 8 : sz.avanceReste);
    doc.setFont("helvetica", "bold"); doc.setTextColor(245, 158, 11);
    doc.text("Avance", margin, y); doc.text(`${fmtPdf(order.amount_paid)} FCFA`, W - margin, y, { align: "right" });
    y += lineH;
    const restant = Number(order.price) - Number(order.amount_paid);
    if (restant > 0) {
      const resteBoxH = isTicket ? 8 : S(28);
      doc.setFillColor(220, 38, 38);
      doc.roundedRect(margin, y - 2, W - margin * 2, resteBoxH, 3, 3, "F");
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold");
      doc.setFontSize(isTicket ? 9 : sz.resteBig);
      doc.text("Reste", margin + 10, y + resteBoxH * 0.68);
      doc.text(`${fmtPdf(restant)} FCFA`, W - margin - 10, y + resteBoxH * 0.68, { align: "right" });
      y += resteBoxH + S(6);
    }
  } else if (order.payment_status === "paye") {
    checkPage(S(28));
    y += isTicket ? 3 : S(8);
    const payeBoxH = isTicket ? 8 : S(22);
    doc.setFillColor(22, 163, 74);
    doc.roundedRect(margin, y - 2, W - margin * 2, payeBoxH, 3, 3, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold");
    doc.setFontSize(isTicket ? 8 : sz.payeBadge);
    doc.text("PAYE", W / 2, y + payeBoxH * 0.64, { align: "center" });
    y += payeBoxH + S(6);
  }

  // QR Code
  const qrImg = await qrDataUrl(order.id);
  if (qrImg) {
    const qrSize = isTicket ? 22 : (sizeProfile === "grand" ? 110 : 80);
    checkPage(qrSize + 40);
    y += 12;
    const pad = 8;
    doc.setFillColor(255, 255, 255);
    doc.rect(W / 2 - qrSize / 2 - pad, y - pad, qrSize + pad * 2, qrSize + pad * 2, "F");
    doc.setDrawColor(160, 160, 160);
    doc.setLineWidth(0.6);
    doc.rect(W / 2 - qrSize / 2 - pad, y - pad, qrSize + pad * 2, qrSize + pad * 2, "S");
    doc.setLineWidth(0.2);
    doc.addImage(qrImg, "PNG", W / 2 - qrSize / 2, y, qrSize, qrSize);
    y += qrSize + pad + 8;
    doc.setFontSize(isTicket ? 7 : sz.qrLabel); doc.setTextColor(80, 90, 110); doc.setFont("helvetica", "bold");
    doc.text("Scannez pour suivre votre commande", W / 2, y, { align: "center" });
    y += isTicket ? 4 : S(16);
  }

  // Footer
  checkPage(S(42));
  doc.setDrawColor(200, 210, 230); doc.setLineWidth(0.4); doc.line(margin, y, W - margin, y); doc.setLineWidth(0.2);
  y += isTicket ? 4 : S(14);
  doc.setTextColor(120, 130, 150); doc.setFontSize(isTicket ? 7 : sz.footerTitle);
  doc.setFont("helvetica", "bold");
  doc.text("Merci de votre confiance !", W / 2, y, { align: "center" });
  y += isTicket ? 4 : S(12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(isTicket ? 6 : sz.footerInfo);
  const _bPhone = (_brandingCache && _brandingCache.phone_main) || "+221 76 809 09 28";
  const footerText = `Setal Express - ${_bPhone} - setalexpress.com`;
  const footerMaxW = W - margin * 2;
  const footerLines = doc.splitTextToSize(footerText, footerMaxW);
  doc.text(footerLines, W / 2, y, { align: "center" });
  y += (isTicket ? 4 : S(10)) * footerLines.length;
  doc.setFontSize(isTicket ? 6 : sz.footerDate);
  doc.text(`Emis le ${new Date().toLocaleDateString("fr-FR")} a ${new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`, W / 2, y, { align: "center" });

  return doc;
}

export async function sendReceiptViaWhatsApp(order, setLoading, setError) {
  setLoading(true);
  if (setError) setError("");
  try {
    // Génération du PDF
    const doc = await generateReceiptPDF(order);
    if (!doc) throw new Error("Impossible de générer le reçu PDF");
    const pdfBase64 = doc.output("datauristring").split(",")[1];
    if (!pdfBase64) throw new Error("Erreur lors de l'export PDF");

    // Envoi via WhatsApp
    await api.sendRecuWhatsApp({
      phone: order.phone,
      pdfBase64,
      filename: `recu-${order.id}.pdf`,
      caption: `Votre reçu Setal Express — commande *${order.id}* (${order.price ? Number(order.price).toLocaleString() + " FCFA" : "en cours"}) 🧺`,
      order_id: order.id,
    });
    alert("Reçu PDF envoyé par WhatsApp.");
  } catch (err) {
    const msg = err.message || "Erreur inconnue";
    if (setError) setError(msg);
    else alert("Envoi échoué : " + msg);
  } finally {
    setLoading(false);
  }
}

// ─── Push Notification Helper ───────────────────────────────────────────────
export async function subscribeToPush(phone) {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return false;

    const reg = await navigator.serviceWorker.ready;
    const vapidData = await api.getVapidKey();
    if (!vapidData.publicKey) return false;

    // Convert VAPID key to Uint8Array
    const padding = "=".repeat((4 - vapidData.publicKey.length % 4) % 4);
    const base64 = (vapidData.publicKey + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);

    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: outputArray,
    });

    await api.subscribePush(phone, subscription.toJSON());
    return true;
  } catch (err) {
    console.error("Push subscription error:", err);
    return false;
  }
}
